<template>
    <div class="carbon-page-header">
        <!-- ── Left: Identity block ─────────────────────────────── -->
        <div class="header-left">
            <h1 class="page-title">탄소 배출 관리</h1>
            <p class="page-context" v-if="contextLabel">{{ contextLabel }}</p>
        </div>

        <!-- ── Right: Frosted glass toolbar pill ───────────────── -->
        <div class="header-right">
            <div class="toolbar-pill">
                <!-- 기관 선택 -->
                <select
                    :value="selectedOrgIdx"
                    class="header-select"
                    title="기관"
                    @change="$emit('update:selectedOrgIdx', $event.target.value ? Number($event.target.value) : null)"
                >
                    <option :value="null" disabled>기관 선택</option>
                    <option v-for="org in orgList" :key="org.orgIdx" :value="org.orgIdx">
                        {{ org.orgName || org.orgCode || org.orgIdx }}
                    </option>
                </select>

                <!-- 연간/월간 토글 -->
                <div class="view-mode-toggle">
                    <button
                        type="button"
                        :class="['toggle-btn', { active: viewMode === 'YEAR' }]"
                        @click="$emit('update:viewMode', 'YEAR')"
                    >
                        연간
                    </button>
                    <button
                        type="button"
                        :class="['toggle-btn', { active: viewMode === 'MONTH' }]"
                        @click="$emit('update:viewMode', 'MONTH')"
                    >
                        월간
                    </button>
                </div>

                <!-- 연도/월 picker -->
                <div
                    class="header-year-picker-wrapper"
                    :class="{ loading: dashboardLoading }"
                    v-click-outside="handleCloseYearDropdown"
                >
                    <button
                        type="button"
                        class="year-nav-btn"
                        :disabled="dashboardLoading"
                        @click="viewMode === 'MONTH' ? navigateMonth(-1) : navigateYear(-1)"
                        :title="viewMode === 'MONTH' ? '이전 월' : '이전 연도'"
                    >
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <div class="year-display" @click="!dashboardLoading && handleToggleYearDropdown()">
                        <span v-if="viewMode === 'YEAR'">{{ selectedYear }}년</span>
                        <span v-else>{{ periodDisplayText }}</span>
                        <i v-if="dashboardLoading" class="fas fa-spinner fa-spin year-loading-icon"></i>
                    </div>
                    <button
                        type="button"
                        class="year-nav-btn"
                        :disabled="dashboardLoading"
                        @click="viewMode === 'MONTH' ? navigateMonth(1) : navigateYear(1)"
                        :title="viewMode === 'MONTH' ? '다음 월' : '다음 연도'"
                    >
                        <i class="fas fa-chevron-right"></i>
                    </button>

                    <!-- 연도 드롭다운 -->
                    <div v-if="showYearDropdown && viewMode === 'YEAR'" class="year-dropdown">
                        <div class="year-range-header">
                            <button type="button" class="year-range-btn" @click="prevYearRange" title="이전 구간">
                                <i class="fas fa-chevron-left"></i>
                            </button>
                            <span class="year-range-label"
                                >{{ dropdownViewStartYear }} - {{ dropdownViewStartYear + 11 }}</span
                            >
                            <button type="button" class="year-range-btn" @click="nextYearRange" title="다음 구간">
                                <i class="fas fa-chevron-right"></i>
                            </button>
                        </div>
                        <div class="year-grid">
                            <button
                                v-for="y in yearGrid"
                                :key="y"
                                type="button"
                                :class="['year-cell', { active: String(y) === selectedYear }]"
                                @click="selectYear(y)"
                            >
                                {{ y }}
                            </button>
                        </div>
                    </div>

                    <!-- 월 선택 드롭다운 -->
                    <div v-if="showYearDropdown && viewMode === 'MONTH'" class="year-dropdown month-dropdown">
                        <div class="year-range-header">
                            <button type="button" class="year-range-btn" @click="prevMonthYearRange" title="이전 연도">
                                <i class="fas fa-chevron-left"></i>
                            </button>
                            <span class="year-range-label">{{ dropdownMonthYear }}년</span>
                            <button type="button" class="year-range-btn" @click="nextMonthYearRange" title="다음 연도">
                                <i class="fas fa-chevron-right"></i>
                            </button>
                        </div>
                        <div class="month-grid">
                            <button
                                v-for="m in 12"
                                :key="m"
                                type="button"
                                :class="[
                                    'year-cell month-cell',
                                    { active: selectedMonth === monthToYYYYMM(dropdownMonthYear, m) },
                                ]"
                                @click="selectMonth(dropdownMonthYear, m)"
                            >
                                {{ m }}월
                            </button>
                        </div>
                    </div>
                </div>

                <!-- 단위 토글 -->
                <div class="view-mode-toggle unit-toggle">
                    <button
                        type="button"
                        :class="['toggle-btn', { active: displayUnit === 'kgCO2e' }]"
                        @click="$emit('update:displayUnit', 'kgCO2e')"
                    >
                        kg
                    </button>
                    <button
                        type="button"
                        :class="['toggle-btn', { active: displayUnit === 'tCO2e' }]"
                        @click="$emit('update:displayUnit', 'tCO2e')"
                    >
                        t
                    </button>
                </div>

                <!-- 액션 구분선 -->
                <span class="toolbar-divider"></span>

                <!-- 보고서 -->
                <button class="action-btn" @click="handleReportClick" title="보고서">
                    <i class="fas fa-file-alt"></i>
                </button>
                <!-- 설정 -->
                <button class="action-btn settings-btn" @click="handleSettingsClick" title="설정">
                    <i class="fas fa-cog"></i>
                </button>
                <!-- 갱신 -->
                <button
                    type="button"
                    class="action-btn"
                    :disabled="dashboardLoading"
                    title="갱신"
                    @click="$emit('refresh')"
                >
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': dashboardLoading }"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CarbonHeader",
    directives: {
        clickOutside: {
            bind(el, binding) {
                el._clickOutside = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener("click", el._clickOutside);
            },
            unbind(el) {
                document.body.removeEventListener("click", el._clickOutside);
            },
        },
    },
    props: {
        orgList: { type: Array, default: () => [] },
        selectedOrgIdx: { type: Number, default: null },
        selectedYear: {
            type: String,
            default: () => String(new Date().getFullYear()),
        },
        selectedMonth: {
            type: String,
            default: () => {
                const d = new Date();
                return String(d.getFullYear()) + String(d.getMonth() + 1).padStart(2, "0");
            },
        },
        viewMode: {
            type: String,
            default: "YEAR",
            validator: (v) => ["YEAR", "MONTH"].includes(v),
        },
        displayUnit: {
            type: String,
            default: "tCO2e",
            validator: (v) => ["kgCO2e", "tCO2e"].includes(v),
        },
        dashboardLoading: { type: Boolean, default: false },
    },
    data() {
        return {
            showYearDropdown: false,
            dropdownViewStartYear: 2020,
        };
    },
    computed: {
        yearGrid() {
            const start = this.dropdownViewStartYear;
            return Array.from({ length: 12 }, (_, i) => start + i);
        },
        periodDisplayText() {
            const ym = this.selectedMonth || "";
            if (ym.length >= 6) {
                const y = ym.slice(0, 4);
                const m = parseInt(ym.slice(4, 6), 10);
                return `${y}년 ${m}월`;
            }
            return `${this.selectedYear}년`;
        },
        dropdownMonthYear() {
            return this.dropdownViewStartYear;
        },
        contextLabel() {
            const org = this.orgList.find((o) => o.orgIdx === this.selectedOrgIdx);
            const orgName = org ? org.orgName || org.orgCode || "" : "";
            const period = this.viewMode === "YEAR" ? `${this.selectedYear}년` : this.periodDisplayText;
            return orgName ? `${orgName} · ${period}` : period;
        },
    },
    methods: {
        handleToggleYearDropdown() {
            if (!this.showYearDropdown) {
                if (this.viewMode === "MONTH") {
                    const ym = this.selectedMonth || "";
                    const y =
                        ym.length >= 4
                            ? parseInt(ym.slice(0, 4), 10)
                            : parseInt(this.selectedYear, 10) || new Date().getFullYear();
                    this.dropdownViewStartYear = Math.max(2000, Math.min(2100, y));
                } else {
                    const y = parseInt(this.selectedYear, 10) || new Date().getFullYear();
                    this.dropdownViewStartYear = 2000 + 12 * Math.floor((y - 2000) / 12);
                    this.dropdownViewStartYear = Math.max(2000, Math.min(2096, this.dropdownViewStartYear));
                }
            }
            this.showYearDropdown = !this.showYearDropdown;
        },
        handleCloseYearDropdown() {
            this.showYearDropdown = false;
        },
        prevYearRange() {
            this.dropdownViewStartYear = Math.max(2000, this.dropdownViewStartYear - 12);
        },
        nextYearRange() {
            this.dropdownViewStartYear = Math.min(2096, this.dropdownViewStartYear + 12);
        },
        selectYear(year) {
            const clamped = Math.max(2000, Math.min(2100, year));
            this.$emit("update:selectedYear", String(clamped));
            this.showYearDropdown = false;
        },
        navigateYear(delta) {
            const current = parseInt(this.selectedYear, 10) || new Date().getFullYear();
            const next = Math.max(2000, Math.min(2100, current + delta));
            this.$emit("update:selectedYear", String(next));
        },
        monthToYYYYMM(year, month) {
            return String(year) + String(month).padStart(2, "0");
        },
        navigateMonth(delta) {
            const ym = this.selectedMonth || "";
            if (ym.length < 6) {
                const y = parseInt(this.selectedYear, 10) || new Date().getFullYear();
                const m = new Date().getMonth() + 1;
                const next = this.monthToYYYYMM(y, m);
                this.$emit("update:selectedMonth", next);
                this.$emit("update:selectedYear", String(y));
                return;
            }
            const y = parseInt(ym.slice(0, 4), 10);
            const m = parseInt(ym.slice(4, 6), 10);
            let nextM = m + delta;
            let nextY = y;
            if (nextM > 12) {
                nextM = 1;
                nextY += 1;
            } else if (nextM < 1) {
                nextM = 12;
                nextY -= 1;
            }
            nextY = Math.max(2000, Math.min(2100, nextY));
            const next = this.monthToYYYYMM(nextY, nextM);
            this.$emit("update:selectedMonth", next);
            this.$emit("update:selectedYear", String(nextY));
        },
        selectMonth(year, month) {
            const yyyymm = this.monthToYYYYMM(year, month);
            this.$emit("update:selectedMonth", yyyymm);
            this.$emit("update:selectedYear", String(year));
            this.showYearDropdown = false;
        },
        prevMonthYearRange() {
            this.dropdownViewStartYear = Math.max(2000, this.dropdownViewStartYear - 1);
        },
        nextMonthYearRange() {
            this.dropdownViewStartYear = Math.min(2100, this.dropdownViewStartYear + 1);
        },
        handleSettingsClick() {
            this.$emit("settings-click");
        },
        handleReportClick() {
            this.$emit("report-click");
        },
    },
};
</script>

<style scoped>
/* ── Page header: editorial — transparent, no card ── */
.carbon-page-header {
    padding: 4px 2px 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: transparent;
    border: none;
    flex-shrink: 0;
    box-sizing: border-box;
    position: relative;
    z-index: 50;
    overflow: visible;
}

/* ── Left: identity block ──────────────────────────────────── */
.header-left {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex: 1;
    min-width: 0;
}

.page-title {
    font-size: 24px;
    font-weight: 800;
    margin: 0 !important;
    letter-spacing: -0.025em;
    color: rgba(17, 22, 26, 0.42);

    line-height: 1.1;
}

.page-context {
    font-size: 12px;
    font-weight: 500;
    color: rgba(17, 22, 26, 0.42);
    letter-spacing: 0.01em;
    margin: 0;
    line-height: 1;
}

/* ── Right: anchor ─────────────────────────────────────────── */
.header-right {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
}

/* ── Frosted glass toolbar pill ────────────────────────────── */
.toolbar-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.72);
    -webkit-backdrop-filter: blur(16px) saturate(160%);
    backdrop-filter: blur(16px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.88);
    border-radius: 14px;
    padding: 5px 6px;
    box-shadow:
        0 2px 12px rgba(0, 0, 0, 0.08),
        0 1px 0 rgba(255, 255, 255, 0.8) inset;
}

/* Divider between context controls and action buttons */
.toolbar-divider {
    width: 1px;
    height: 18px;
    background: rgba(17, 22, 26, 0.12);
    flex-shrink: 0;
    align-self: center;
}

/* ── 기관 선택 ──────────────────────────────────────────────── */
.header-select {
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(17, 22, 26, 0.85);
    background: rgba(17, 22, 26, 0.05);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    min-width: 0;
    font-family: inherit;
    transition:
        border-color 0.15s,
        background 0.15s;
}
.header-select:hover {
    border-color: oklch(0.5 0.09 168);
    background: rgba(17, 22, 26, 0.07);
}
.header-select:focus {
    outline: none;
    border-color: oklch(0.5 0.09 168);
}
.header-select option {
    background: #fff;
    color: #11161a;
}

/* ── 연간/월간 + 단위 토글 pill ────────────────────────────── */
.view-mode-toggle {
    display: inline-flex;
    align-items: center;
    background: rgba(17, 22, 26, 0.06);
    border-radius: 999px;
    padding: 2px;
    border: none;
    gap: 0;
}
.toggle-btn {
    padding: 4px 11px;
    font-size: 11px;
    font-weight: 600;
    color: rgba(17, 22, 26, 0.45);
    background: transparent;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s,
        box-shadow 0.15s;
    white-space: nowrap;
    font-family: inherit;
}
.toggle-btn:hover {
    color: rgba(17, 22, 26, 0.88);
}
.toggle-btn.active {
    background: rgba(255, 255, 255, 0.9);
    color: oklch(0.5 0.09 168);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

/* ── 연도/월 picker ─────────────────────────────────────────── */
.header-year-picker-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    background: rgba(17, 22, 26, 0.06);
    border-radius: 999px;
    padding: 1px 4px;
    border: none;
    z-index: 100;
    transition: border-color 0.15s;
}
.header-year-picker-wrapper:focus-within {
    border-color: oklch(0.5 0.09 168);
}
.header-year-picker-wrapper.loading {
    opacity: 0.6;
    pointer-events: none;
}

.year-nav-btn {
    background: transparent;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    color: rgba(17, 22, 26, 0.5);
    transition:
        color 0.15s,
        background 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    font-family: inherit;
}
.year-nav-btn:hover:not(:disabled) {
    color: rgba(17, 22, 26, 0.88);
    background: rgba(17, 22, 26, 0.07);
}
.year-nav-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.year-display {
    padding: 4px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 70px;
    justify-content: center;
    border-radius: 999px;
    color: rgba(17, 22, 26, 0.88);
    font-weight: 700;
    font-size: 13px;
    transition: background 0.15s;
    letter-spacing: -0.01em;
}
.year-display:hover {
    background: rgba(17, 22, 26, 0.06);
}
.year-loading-icon {
    font-size: 10px;
    color: rgba(17, 22, 26, 0.35);
}

/* ── Action buttons (icon-only) ─────────────────────────────── */
.action-btn {
    padding: 0 9px;
    height: 30px;
    background: transparent;
    color: rgba(17, 22, 26, 0.45);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    transition:
        background 0.15s,
        color 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
}
.action-btn i {
    font-size: 13px;
}
.action-btn:focus,
.action-btn:active {
    outline: none;
}
.action-btn:hover:not(:disabled) {
    background: rgba(17, 22, 26, 0.08);
    color: rgba(17, 22, 26, 0.88);
}
.action-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

/* ── Year/month dropdown panel ──────────────────────────────── */
.year-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: rgba(255, 255, 255, 0.96);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 14px;
    box-shadow:
        0 8px 32px rgba(17, 22, 26, 0.14),
        0 2px 8px rgba(17, 22, 26, 0.06);
    z-index: 2200;
    padding: 14px;
    min-width: 240px;
}

.year-range-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(17, 22, 26, 0.08);
}

.year-range-btn {
    background: rgba(17, 22, 26, 0.05);
    border: none;
    padding: 4px 10px;
    cursor: pointer;
    color: rgba(17, 22, 26, 0.55);
    border-radius: 8px;
    font-size: 12px;
    transition:
        background 0.15s,
        color 0.15s;
    font-family: inherit;
}
.year-range-btn:hover {
    background: oklch(0.5 0.09 168);
    color: #fff;
    border-color: oklch(0.5 0.09 168);
}

.year-range-label {
    font-size: 13px;
    font-weight: 700;
    color: rgba(17, 22, 26, 0.88);
}

.year-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 5px;
}

.year-cell {
    padding: 7px 4px;
    border: 1px solid rgba(17, 22, 26, 0.1);
    border-radius: 8px;
    background: rgba(17, 22, 26, 0.03);
    color: rgba(17, 22, 26, 0.65);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s,
        border-color 0.15s;
    font-family: inherit;
    text-align: center;
}
.year-cell:hover {
    background: oklch(0.94 0.025 168);
    border-color: oklch(0.88 0.04 168);
    color: oklch(0.5 0.09 168);
}
.year-cell.active {
    background: oklch(0.5 0.09 168);
    border-color: oklch(0.5 0.09 168);
    color: #fff;
}

.month-dropdown .month-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 5px;
}
.month-cell {
    padding: 7px 4px;
}

/* ── Responsive ─────────────────────────────────────────────── */
@media (max-width: 768px) {
    .carbon-page-header {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
    .header-right {
        width: 100%;
    }
    .toolbar-pill {
        flex-wrap: wrap;
        gap: 6px;
        width: 100%;
    }
}
</style>
